import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './auth/AuthContext';
import { VerifyEmailNeededScreen } from './screens/auth/VerifyEmailNeededScreen';
import { useApi } from './hooks/useApi';
import { isDeferredMode } from './lib/deferredBaseline';
import { AppShell } from './components/layout/AppShell';
import { LoginScreen } from './screens/login/LoginScreen';
import { RegisterScreen } from './screens/register/RegisterScreen';
// TodayScreen + TaperScreen are the P0 surfaces a returning user lands on
// almost every visit. Keeping them in the main authed bundle avoids the
// Suspense flash on the most common path. Every other authed screen is
// lazy-loaded below — see react.dev/reference/react/lazy and the
// jsmanifest "code-splitting in 2026" pattern.
import { TodayScreen } from './screens/today/TodayScreen';
import { TaperScreen } from './screens/taper/TaperScreen';
// LogDoseSheet is mounted alongside the routes (not a route itself) and
// fires from a global FAB. It stays eager so the first-time tap doesn't
// pay a fetch round-trip on top of the sheet animation.
import { LogDoseSheet } from './screens/log/LogDoseSheet';
import { MotivationalToast } from './components/domain/MotivationalToast';
import { usePopup } from './hooks/usePopup';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RouteFallback } from './components/ui/RouteFallback';
import type { TaperPlanWire } from '@shared/types';

// Authed screens — lazy. Each entry pulls its module into its own chunk,
// keeping the index bundle below the 500 KB budget and trimming the
// main-thread parse/eval cost for users who never visit a given surface.
// All screens use named exports, so we adapt to React.lazy's
// `default`-only contract via the `.then(m => ({ default: m.X }))` shape.
//
// Loaders are extracted into named arrows so the same dynamic import
// can be fired ahead of time from the TabBar (preload-on-hover). The
// browser dedupes a repeat import call, so the runtime click cost is
// zero once the chunk is cached. See PRELOADERS export below.
const loadOnboarding = () =>
  import('./screens/onboarding/OnboardingScreen').then((m) => ({ default: m.OnboardingScreen }));
const loadInsights = () =>
  import('./screens/insights/InsightsScreen').then((m) => ({ default: m.InsightsScreen }));
const loadCommunity = () =>
  import('./screens/community/CommunityScreen').then((m) => ({ default: m.CommunityScreen }));
const loadPeer = () =>
  import('./screens/community/PeerScreen').then((m) => ({ default: m.PeerScreen }));
const loadMilestoneWall = () =>
  import('./screens/community/MilestoneWallScreen').then((m) => ({ default: m.MilestoneWallScreen }));
const loadStories = () =>
  import('./screens/community/StoriesScreen').then((m) => ({ default: m.StoriesScreen }));
const loadChat = () =>
  import('./screens/chat/ChatScreen').then((m) => ({ default: m.ChatScreen }));
const loadBuddy = () =>
  import('./screens/buddy/BuddyScreen').then((m) => ({ default: m.BuddyScreen }));
const loadBuddyHistory = () =>
  import('./screens/buddy/BuddyHistoryScreen').then((m) => ({ default: m.BuddyHistoryScreen }));
const loadIntentions = () =>
  import('./screens/buddy/IntentionsScreen').then((m) => ({ default: m.IntentionsScreen }));
const loadBuddyMemoryReview = () =>
  import('./screens/buddy/BuddyMemoryReviewScreen').then((m) => ({
    default: m.BuddyMemoryReviewScreen,
  }));
const loadLearn = () =>
  import('./screens/learn/LearnScreen').then((m) => ({ default: m.LearnScreen }));
const loadSettings = () =>
  import('./screens/settings/SettingsScreen').then((m) => ({ default: m.SettingsScreen }));
const loadAppearance = () =>
  import('./screens/settings/AppearanceScreen').then((m) => ({ default: m.AppearanceScreen }));
const loadSecurity = () =>
  import('./screens/settings/SecurityScreen').then((m) => ({ default: m.SecurityScreen }));
const loadPrivacyData = () =>
  import('./screens/PrivacyDataScreen').then((m) => ({ default: m.PrivacyDataScreen }));
const loadCheckIn = () =>
  import('./screens/checkin/CheckInScreen').then((m) => ({ default: m.CheckInScreen }));
const loadUrgeSurf = () =>
  import('./screens/urge-surf/UrgeSurfScreen').then((m) => ({ default: m.UrgeSurfScreen }));
const loadBreathe = () =>
  import('./screens/breathe/BreatheScreen').then((m) => ({ default: m.BreatheScreen }));
const loadMindTricks = () =>
  import('./screens/mind-tricks/MindTricksScreen').then((m) => ({ default: m.MindTricksScreen }));

const OnboardingScreen = lazy(loadOnboarding);
const InsightsScreen = lazy(loadInsights);
const CommunityScreen = lazy(loadCommunity);
const PeerScreen = lazy(loadPeer);
const MilestoneWallScreen = lazy(loadMilestoneWall);
const StoriesScreen = lazy(loadStories);
const ChatScreen = lazy(loadChat);
const BuddyScreen = lazy(loadBuddy);
const BuddyHistoryScreen = lazy(loadBuddyHistory);
const IntentionsScreen = lazy(loadIntentions);
const BuddyMemoryReviewScreen = lazy(loadBuddyMemoryReview);
const LearnScreen = lazy(loadLearn);
const SettingsScreen = lazy(loadSettings);
const AppearanceScreen = lazy(loadAppearance);
const SecurityScreen = lazy(loadSecurity);
const PrivacyDataScreen = lazy(loadPrivacyData);
const CheckInScreen = lazy(loadCheckIn);
const UrgeSurfScreen = lazy(loadUrgeSurf);
const BreatheScreen = lazy(loadBreathe);
const MindTricksScreen = lazy(loadMindTricks);

/**
 * Map of route paths → dynamic-import loaders. Wire `onPointerEnter` /
 * `onFocus` on a navigation control to call `PRELOADERS[path]?.()` so the
 * chunk lands in cache before the user clicks. Safe to call repeatedly;
 * the browser dedupes a second import of the same module.
 *
 * Limited to high-probability targets reached from the TabBar / Today
 * CTAs — adding more is cheap (one entry per route), the cost is one
 * extra round-trip per hover that didn't convert to a click.
 */
export const PRELOADERS: Record<string, () => Promise<unknown>> = {
  '/app/buddy': loadBuddy,
  '/app/insights': loadInsights,
  '/app/community': loadCommunity,
  '/app/checkin': loadCheckIn,
  '/app/more': loadSettings,
  '/app/urge': loadUrgeSurf,
  '/app/breathe': loadBreathe,
  '/app/mind-tricks': loadMindTricks,
  '/app/learn': loadLearn,
};
const ChangelogScreen = lazy(() =>
  import('./screens/changelog/ChangelogScreen').then((m) => ({ default: m.ChangelogScreen })),
);
const JournalScreen = lazy(() =>
  import('./screens/journal/JournalScreen').then((m) => ({ default: m.JournalScreen })),
);
const HistoryScreen = lazy(() =>
  import('./screens/history/HistoryScreen').then((m) => ({ default: m.HistoryScreen })),
);
const RecoveryScreen = lazy(() =>
  import('./screens/recovery/RecoveryScreen').then((m) => ({ default: m.RecoveryScreen })),
);

// Landing + legal pages are public, marketing-sized, and only relevant to
// guests. Lazy-loaded so authed users (who go straight to /app) don't pay
// for the marketing bundle.
const LandingScreen = lazy(() =>
  import('./screens/landing/LandingScreen').then((m) => ({ default: m.LandingScreen })),
);
const PrivacyScreen = lazy(() =>
  import('./screens/legal/PrivacyScreen').then((m) => ({ default: m.PrivacyScreen })),
);
const TermsScreen = lazy(() =>
  import('./screens/legal/TermsScreen').then((m) => ({ default: m.TermsScreen })),
);
const DisclaimerScreen = lazy(() =>
  import('./screens/legal/DisclaimerScreen').then((m) => ({ default: m.DisclaimerScreen })),
);
const FAQPage = lazy(() =>
  import('./screens/landing/FAQPage').then((m) => ({ default: m.FAQPage })),
);
const HelpPage = lazy(() =>
  import('./screens/help/HelpPage').then((m) => ({ default: m.HelpPage })),
);
const KnowledgePage = lazy(() =>
  import('./screens/knowledge/KnowledgePage').then((m) => ({
    default: m.KnowledgePage,
  })),
);
const KnowledgeEntryPage = lazy(() =>
  import('./screens/knowledge/KnowledgeEntryPage').then((m) => ({
    default: m.KnowledgeEntryPage,
  })),
);
const AdminScreen = lazy(() =>
  import('./screens/admin/AdminScreen').then((m) => ({ default: m.AdminScreen })),
);
// Letters to future you — Phase C.2 §P10.7. Lazy so the recorder
// bundle doesn't ship to users who never open the surface.
const LettersScreen = lazy(() => import('./screens/letters/LettersScreen'));
const LetterComposer = lazy(() => import('./screens/letters/LetterComposer'));
// Phase D.2 §P10.9 — 2-page Minimum-Viable Check-In. Lazy so the
// main authed bundle doesn't pay for a parallel path most nights
// the user opts out of.
const MinimumCheckin = lazy(() => import('./screens/checkin/MinimumCheckin'));
// Phase 11 Wave 2 — Plus paywall. Lazy so the Stripe-flavored billing
// screen doesn't ship in the main bundle for users who never open it
// (it stays invisible until the `billing_enabled` flag is on).
const UpgradeScreen = lazy(() => import('./screens/billing/UpgradeScreen'));

function Loading() {
  return <RouteFallback />;
}

// Admin-only route guard. Non-admin authed users bounce to /app.
// Guests have already been redirected to /login by AppRoutes above
// this guard, so by the time we get here `me` is guaranteed populated.
function RequireAdmin({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  if (!me?.isAdmin) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

export function AppRoutes() {
  const { status } = useAuth();
  if (status === 'loading') return <Loading />;
  if (status === 'guest') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<LandingScreen />} />
            <Route path="/privacy" element={<PrivacyScreen />} />
            <Route path="/terms" element={<TermsScreen />} />
            <Route path="/disclaimer" element={<DisclaimerScreen />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/knowledge" element={<KnowledgePage />} />
            <Route path="/knowledge/:slug" element={<KnowledgeEntryPage />} />
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/register" element={<RegisterScreen />} />
            {/* Any guest hitting an /app/* URL bounces to login; preserve
                their intended destination so post-login routing can return
                them there. */}
            <Route path="/app/*" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary>
      <AuthedRoutes />
    </ErrorBoundary>
  );
}

// Phase 14 §S1 — paths an unverified user is still allowed to reach.
// Mirrors the guest allowlist for legal pages and gives them an escape
// hatch: they can still read the privacy/terms/disclaimer copy, and
// the logout button on VerifyEmailNeededScreen drops them back to /.
const UNVERIFIED_ALLOWED_PATHS: readonly string[] = [
  '/privacy',
  '/terms',
  '/disclaimer',
];

function AuthedRoutes() {
  const { me, emailVerified } = useAuth();
  const planRes = useApi<TaperPlanWire | null>('/api/taper');
  const location = useLocation();
  const [logOpen, setLogOpen] = useState(false);

  // Motivational popup: fire on initial mount, then re-trigger on key
  // navigations that may have created eligibility (e.g. dose log).
  const popupEnabled = me?.settings.motivationalPopups !== false && !!me?.baselineLocked;
  const { popup, trigger: triggerPopup, dismiss: dismissPopup } = usePopup(popupEnabled);

  // Re-check eligibility when route changes (cheap; server enforces caps).
  useEffect(() => {
    if (popupEnabled) triggerPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Share-target inbox: when the user shares text/URL INTO the app
  // via the OS share sheet, the manifest's share_target lands them on
  // /app with shared_title / shared_text / shared_url query params.
  // We capture those into sessionStorage and open the LogDoseSheet so
  // the content lands in the note field for review.
  //
  // Hardening at the source: each value is NFKD-normalized (collapses
  // compatibility forms / ligatures that could be used for homograph
  // spoofing when the note is later displayed) and capped at 1024 chars
  // before storage. LogDoseSheet applies a tighter 500-char display cap
  // downstream. The provenance banner ("Shared from <source>") is what
  // the user actually sees — these caps are belt-and-suspenders.
  useEffect(() => {
    if (location.pathname !== '/app') return;
    const params = new URLSearchParams(location.search);
    function clean(v: string | null): string | null {
      if (v == null) return null;
      // NFKD then trim; max 1024 chars. The dose-note display cap is
      // tighter still in LogDoseSheet.
      return v.normalize('NFKD').slice(0, 1024);
    }
    const title = clean(params.get('shared_title'));
    const text = clean(params.get('shared_text'));
    const url = clean(params.get('shared_url'));
    if (!title && !text && !url) return;
    try {
      window.sessionStorage.setItem(
        'qk.shareInbox.v1',
        JSON.stringify({ title, text, url }),
      );
    } catch {
      // ignored — best-effort
    }
    // Open the log sheet by setting the hash. Drop the query params from
    // the URL so a reload doesn't re-fire the inbox flow.
    window.history.replaceState(null, '', '/app#log');
    setLogOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Defensive ?action=log handler. The canonical manifest shortcut URL
  // is /app#log (hash-only so the existing #log listener catches it
  // without remounting whatever screen the user was on). But any older
  // installed WebAPK still pinned to /app?action=log will keep landing
  // here for 1–30 days until Chrome re-fetches the manifest. Convert
  // the query form to the canonical hash form on arrival so neither
  // shortcut shape is silently broken.
  useEffect(() => {
    if (location.pathname !== '/app') return;
    const params = new URLSearchParams(location.search);
    if (params.get('action') !== 'log') return;
    window.history.replaceState(null, '', '/app#log');
    setLogOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // launchQueue handler — paired with manifest `client_mode:
  // focus-existing` and the consumer in src/main.tsx. The browser
  // focuses the existing PWA window but does NOT auto-navigate; we
  // get the launch URL via the 'qk:launched' CustomEvent and react
  // in-SPA. Critical behavior: Log/Crisis shortcuts open OVER the
  // current screen (no Today re-mount when tapped from /app/taper);
  // path-based shortcuts use react-router navigate for SPA-clean
  // transitions.
  const navigate = useNavigate();
  useEffect(() => {
    function onLaunched(e: Event) {
      const detail = (e as CustomEvent<{ url?: string }>).detail;
      if (!detail?.url) return;
      let parsed: URL;
      try {
        parsed = new URL(detail.url);
      } catch {
        return;
      }
      // Same-origin only — paranoia against a malformed targetURL.
      if (parsed.origin !== window.location.origin) return;
      const action = new URLSearchParams(parsed.search).get('action');
      // Log shortcut: open the sheet over the current screen.
      // LogDoseSheet is mounted alongside <Routes>, not as a route,
      // so this works regardless of pathname.
      if (parsed.hash === '#log' || action === 'log') {
        setLogOpen(true);
        return;
      }
      // Other shortcuts (check-in, crisis, future): navigate via
      // react-router so hash-only changes don't trigger a full
      // route remount.
      navigate(parsed.pathname + parsed.search + parsed.hash);
    }
    window.addEventListener('qk:launched', onLaunched);
    return () => window.removeEventListener('qk:launched', onLaunched);
  }, [navigate]);

  // Open the log sheet when the URL hash is #log; clear it on close.
  useEffect(() => {
    setLogOpen(location.hash === '#log');
  }, [location.hash]);

  function closeLog() {
    setLogOpen(false);
    if (location.hash === '#log') {
      window.history.replaceState(null, '', location.pathname + location.search);
    }
  }

  // Phase 14 §S1 — email-verification gate. Lands before the onboarding
  // redirect so the user can't sneak into /app/onboarding (which would
  // start the taper plan + display-name flow) without proving they own
  // the address first. Legal pages stay open so they can read the terms
  // they're being asked to accept.
  if (!emailVerified) {
    if (UNVERIFIED_ALLOWED_PATHS.includes(location.pathname)) {
      return (
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/privacy" element={<PrivacyScreen />} />
            <Route path="/terms" element={<TermsScreen />} />
            <Route path="/disclaimer" element={<DisclaimerScreen />} />
          </Routes>
        </Suspense>
      );
    }
    return <VerifyEmailNeededScreen />;
  }

  // If the user has no taper plan yet, route them to onboarding.
  // The login → /app fast path renders Loading until plan resolves.
  if (planRes.status === 'loading') return <Loading />;
  // Phase 3 #15 — when the user has explicitly picked "Just log for
  // now" at the onboarding branch, we let them stay on Today even
  // without a plan. They still need a display name (legal/identity
  // requirement); only the plan-shape is deferred. The marker is in
  // localStorage so it's per-device — a user on a second device would
  // get the standard onboarding redirect, which is the right behavior.
  const deferred = isDeferredMode();
  const needsOnboarding =
    (planRes.status === 'ok' && planRes.data === null && !deferred) ||
    (me && me.displayName == null);

  if (needsOnboarding && location.pathname !== '/app/onboarding') {
    return <Navigate to="/app/onboarding" replace />;
  }

  // Screens that suppress the tab bar.
  const fullScreen =
    location.pathname === '/app/onboarding' ||
    location.pathname === '/app/chat' ||
    location.pathname === '/app/learn' ||
    location.pathname === '/app/checkin' ||
    location.pathname === '/app/checkin/minimum' ||
    location.pathname === '/app/buddy/history' ||
    location.pathname === '/app/buddy/intentions' ||
    location.pathname === '/app/buddy/memory' ||
    location.pathname.startsWith('/app/admin');

  return (
    <>
      {fullScreen ? (
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/app/onboarding" element={<OnboardingScreen />} />
            <Route path="/app/chat" element={<ChatScreen />} />
            <Route path="/app/buddy/history" element={<BuddyHistoryScreen />} />
            <Route path="/app/buddy/intentions" element={<IntentionsScreen />} />
            <Route path="/app/buddy/memory" element={<BuddyMemoryReviewScreen />} />
            <Route path="/app/learn" element={<LearnScreen />} />
            <Route path="/app/checkin" element={<CheckInScreen />} />
            <Route path="/app/checkin/minimum" element={<MinimumCheckin />} />
            {/* AdminScreen handles tab selection from both URL hash
                (#audit, #kb, …) and path segment (/app/admin/kb, …).
                A single glob route covers every admin sub-page. */}
            <Route
              path="/app/admin/*"
              element={
                <RequireAdmin>
                  <AdminScreen />
                </RequireAdmin>
              }
            />
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Routes>
        </Suspense>
      ) : (
        <AppShell>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/app" element={<TodayScreen />} />
              <Route path="/app/taper" element={<TaperScreen />} />
              <Route path="/app/buddy" element={<BuddyScreen />} />
              <Route path="/app/insights" element={<InsightsScreen />} />
              <Route path="/app/community" element={<CommunityScreen />} />
              <Route path="/app/community/peer" element={<PeerScreen />} />
              <Route path="/app/community/milestones" element={<MilestoneWallScreen />} />
              <Route path="/app/community/stories" element={<StoriesScreen />} />
              <Route path="/app/urge" element={<UrgeSurfScreen />} />
              <Route path="/app/breathe" element={<BreatheScreen />} />
              <Route path="/app/mind-tricks" element={<MindTricksScreen />} />
              <Route path="/app/more" element={<SettingsScreen />} />
              <Route path="/app/appearance" element={<AppearanceScreen />} />
              <Route path="/app/security" element={<SecurityScreen />} />
              <Route path="/app/privacy-data" element={<PrivacyDataScreen />} />
              <Route path="/app/letters" element={<LettersScreen />} />
              <Route path="/app/letters/new" element={<LetterComposer />} />
              <Route path="/app/changelog" element={<ChangelogScreen />} />
              <Route path="/app/journal" element={<JournalScreen />} />
              <Route path="/app/history" element={<HistoryScreen />} />
              <Route path="/app/recovery" element={<RecoveryScreen />} />
              <Route path="/app/upgrade" element={<UpgradeScreen />} />
              {/* Knowledge base — the same pages are also mounted under
                  /knowledge for guest/SEO traffic (see guest branch above).
                  Mirroring them under /app/knowledge means in-app deep
                  links (e.g. RelapsePreventionCard → naloxone-locator)
                  resolve inside AppShell instead of falling through the
                  /app/* catch-all to the home redirect. */}
              <Route path="/app/knowledge" element={<KnowledgePage />} />
              <Route path="/app/knowledge/:slug" element={<KnowledgeEntryPage />} />
              {/* Authed users hitting public marketing or auth routes bounce
                  to the app. The landing page handles authed redirect on
                  its own to avoid a double-paint. */}
              <Route path="/" element={<Navigate to="/app" replace />} />
              <Route path="/login" element={<Navigate to="/app" replace />} />
              <Route path="/register" element={<Navigate to="/app" replace />} />
              <Route path="*" element={<Navigate to="/app" replace />} />
            </Routes>
          </Suspense>
        </AppShell>
      )}
      <LogDoseSheet open={logOpen} onOpenChange={(o) => (o ? setLogOpen(true) : closeLog())} />
      {!fullScreen ? <MotivationalToast popup={popup} onDismiss={dismissPopup} /> : null}
    </>
  );
}
